<script setup>
import { ref } from 'vue';
import axios from 'axios';
import { Plus } from '@lucide/vue';

import AddUser from '@/components/permission/AddUser.vue';
import Permission from '@/components/permission/Permission.vue';
import EditPermission from '@/components/permission/EditPermission.vue';
import AddMemberConfirmation from "@/components/permission/AddMemberConfirmation.vue";

// Props
const props = defineProps({
  permissibleType: {
    required: true,
    type: String,
    default: null
  },
  permissibleId: {
    required: true,
    type: Number,
    default: null
  },
  userResourceUrl: {
    required: true,
    type: String,
    default: null
  },
  permissionResourceUrl: {
    required: true,
    type: String,
    default: null
  },
  userInvitationUrl: {
    required: true,
    type: String,
    default: null
  },
  permissions: {
    default: () => []
  },
  abilities: {
    default: () => []
  },
  readOnly: {
    type: Boolean,
    default: false
  },
  defaultLocale: {
    type: String,
    default: 'en'
  },
  companyName: {
    type: String,
    default: ''
  }
});

// Data
const perms = ref([...props.permissions]);
const addMemberError = ref(null);
const showUserModal = ref(false);
const showAddMemberConfirmation = ref(false);
const showPermissionsModal = ref(false);

const company = ref(props.companyName);
const permissionEdited = ref(null);
const editPermissionError = ref(null);

// Methods
const addMember = (user) => {
  axios.post(props.permissionResourceUrl, {
    permissible_type: props.permissibleType,
    permissible_id: props.permissibleId,
    user: user.id,
  }).then((response) => {
    onMemberAdded(response.data.data);
  }).catch((error) => {
    addMemberError.value = error.response.data.message?.user?.[0] || error.response.data.message || 'An error occurred while adding the member.';
  });
};

/**
 * An invited member is already in the list: their account and this permission were created
 * by the same request, before the invitation email went out.
 */
const onMemberAdded = (permission) => {
  perms.value.push(permission);
  showUserModal.value = false;
  showAddMemberConfirmation.value = true;
  permissionEdited.value = permission;
};

const onInvitationResent = (permission, user) => {
  const permissionIndex = perms.value.findIndex(p => p.id === permission.id);

  if (permissionIndex !== -1) {
    perms.value.splice(permissionIndex, 1, { ...perms.value[permissionIndex], user });
  }
};

const onEditPermission = (permission) => {
  permissionEdited.value = permission;
  showPermissionsModal.value = true;
};

const onEditPermissions = () => {
  showPermissionsModal.value = true;
}

const onCloseAddMemberConfirmation = () => {
  showAddMemberConfirmation.value = false;
};

const updatePermission = (permission) => {
  editPermissionError.value = null;
  axios.put(`${props.permissionResourceUrl}/${permission.id}`, permission)
    .then((response) => {
      const permissionIndex = perms.value.findIndex(p => p.id === permission.id);
      perms.value.splice(permissionIndex, 1, response.data.data);
      permissionEdited.value = null;
      showPermissionsModal.value = false;
    })
    .catch((error) => {
      editPermissionError.value = error.response.data.message;
    });
};

const deletePermission = (permission) => {
  editPermissionError.value = null;
  axios.delete(`${props.permissionResourceUrl}/${permission.id}`)
    .then(() => {
      const permissionIndex = perms.value.findIndex(p => p.id === permission.id);
      perms.value.splice(permissionIndex, 1);
      permissionEdited.value = null;
      showPermissionsModal.value = false;
    })
    .catch((error) => {
      editPermissionError.value = error.response.data.message;
    });
};
</script>

<template>
  <div>
    <div class="tw:flex tw:gap-6 tw:items-center tw:justify-between">
      <div>
        <h4>
          {{ $t('Team Members') }}
        </h4>

        <div class="text-muted">
          {{ perms?.length }} {{ $t('members') }}
        </div>
      </div>
      <div class="tw:flex tw:justify-end">
        <button class="btn btn-primary tw:flex tw:items-center" v-on:click="showUserModal = true" :disabled="readOnly">
          <Plus class="tw:w-4 tw:h-4 tw:mr-1" />
          {{ $t("Add member") }}
        </button>
      </div>
    </div>

    <div>
      <div v-if="perms?.length > 0" class="mt-3">
        <div class="tw:overflow-x-auto">
          <table class="table tw:border-b tw:border-gray-200">
            <thead>
            <tr class="tw:text-sm">
              <th class="tw:text-left tw:font-medium tw:text-zinc-600 tw:border-b-0 tw:border-t-0 tw:whitespace-nowrap py-2 tw:w-2/5">{{$t('Member')}}</th>
              <th class="tw:text-left tw:font-medium tw:text-zinc-600 tw:border-b-0 tw:border-t-0 tw:whitespace-nowrap py-2 tw:w-1/5">{{$t('Access Type')}}</th>
              <th class="tw:text-left tw:font-medium tw:text-zinc-600 tw:border-b-0 tw:border-t-0 tw:whitespace-nowrap py-2 tw:w-2/5">{{$t('Permissions')}}</th>
              <template v-if="!readOnly">
                <th class="tw:text-left tw:font-medium tw:text-zinc-600 tw:border-b-0 tw:border-t-0 tw:whitespace-nowrap py-2">
                  {{$t('Actions')}}
                </th>
              </template>
            </tr>
            </thead>
            <tbody>
            <Permission v-for="permission in perms" :key="permission.id" :permission-resource-url="permissionResourceUrl"
                        :user-invitation-url="userInvitationUrl" :permissible-type="permissibleType"
                        :permissible-id="permissibleId"
                        :permission="permission" :abilities="abilities" @edit="onEditPermission(permission)"
                        @invitationResent="onInvitationResent(permission, $event)"
                        @delete="deletePermission(permission)" :read-only="readOnly">
            </Permission>
            </tbody>
          </table>
        </div>
      </div>
      <EditPermission :permission="permissionEdited" :abilities="abilities" :show="showPermissionsModal"
                      :error="editPermissionError" v-on:closed="showPermissionsModal = false; editPermissionError = null"
                      v-on:save="updatePermission($event)" v-on:delete="deletePermission(permissionEdited)">
      </EditPermission>
      <AddUser :user-resource-url="userResourceUrl" :user-invitation-url="userInvitationUrl"
               :permissible-type="permissibleType" :permissible-id="permissibleId"
               :show="showUserModal" :error="addMemberError"
               :default-locale="defaultLocale" :company-name="company" v-on:close="showUserModal = false"
               @userSelected="addMember($event)" @memberInvited="onMemberAdded($event)">
      </AddUser>

      <AddMemberConfirmation :show="showAddMemberConfirmation" @close="onCloseAddMemberConfirmation"
                             @editPermissions="onEditPermissions" />
    </div>
  </div>
</template>
